import { useState, useEffect, createContext, useContext } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import type { User as AppUser } from '../types/database'

interface AuthContextType {
  user: User | null
  appUser: AppUser | null
  session: Session | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<{ data?: any; error: any }>
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<{ data?: any; error: any }>
  signOut: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const useAuthProvider = () => {
  const [user, setUser] = useState<User | null>(null)
  const [appUser, setAppUser] = useState<AppUser | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  const refreshUser = async () => {
    if (!user) {
      setAppUser(null)
      return
    }

    try {
      console.log('🔄 Refreshing user data for:', user.id)
      
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select(`
          *,
          companies (
            id,
            name,
            industry
          )
        `)
        .eq('id', user.id)
        .maybeSingle()
      
      if (userError) {
        console.error('❌ Error fetching user:', userError)
        setAppUser(null)
        return
      }

      if (!userData) {
        console.log('⚠️ User not found in users table')
        setAppUser(null)
        return
      }

      console.log('✅ User data found:', userData)
      setAppUser(userData)
    } catch (error) {
      console.error('❌ Error in refreshUser:', error)
      setAppUser(null)
    }
  }

  useEffect(() => {
    console.log('🔐 Initializing auth...')
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('📋 Initial session:', session?.user?.id || 'No session')
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔄 Auth state changed:', event, session?.user?.id || 'No user')
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (user) {
      const timer = setTimeout(() => {
        refreshUser()
      }, 100) // Reduced delay for faster loading
      return () => clearTimeout(timer)
    } else {
      setAppUser(null)
    }
  }, [user])

  const signIn = async (email: string, password: string) => {
    try {
      console.log('🔐 Attempting sign in for:', email)
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      
      if (error) {
        console.error('❌ Sign in error:', error)
        return { data, error }
      }

      console.log('✅ Sign in successful:', data.user?.id)
      return { data, error }
    } catch (error) {
      console.error('❌ Sign in exception:', error)
      return { error }
    }
  }

  const signUp = async (email: string, password: string, metadata?: Record<string, any>) => {
    try {
      console.log('📝 Attempting sign up for:', email)
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
        },
      })
      
      if (error) {
        console.error('❌ Sign up error:', error)
        return { data, error }
      }

      console.log('✅ Sign up successful:', data.user?.id)
      return { data, error }
    } catch (error) {
      console.error('❌ Sign up exception:', error)
      return { error }
    }
  }

  const signOut = async () => {
    try {
      console.log('🚪 Signing out...')
      await supabase.auth.signOut()
      setAppUser(null)
      console.log('✅ Sign out successful')
    } catch (error) {
      console.error('❌ Sign out error:', error)
    }
  }

  return {
    user,
    appUser,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    refreshUser,
  }
}

export { AuthContext }